import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { subscriptionsApi, emailApi, scanApi } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { 
  Search, 
  Plus, 
  Filter,
  MoreVertical,
  XCircle,
  MessageSquare,
  Trash2,
  Eye,
  AlertCircle,
  Loader2,
  FileText,
  Mail,
  Upload,
  X,
  CheckCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const Subscriptions = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredSubs, setFilteredSubs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Report missed subscription modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportForm, setReportForm] = useState({
    service_name: '',
    estimated_amount: '',
    notes: ''
  });
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  
  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, subscription: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Scan PDF Sheet state
  const [pdfSheetOpen, setPdfSheetOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const fileInputRef = useRef(null);

  // Scan Emails Sheet state
  const [emailSheetOpen, setEmailSheetOpen] = useState(false);
  const [emailConnections, setEmailConnections] = useState([]);
  const [isLoadingConnections, setIsLoadingConnections] = useState(false);
  const [scanningConnectionId, setScanningConnectionId] = useState(null);
  const [emailScanResults, setEmailScanResults] = useState({});

  // Review Modal state (for both PDF and Email scans)
  const [reviewModal, setReviewModal] = useState({ open: false, subscriptions: [], source: '' });
  const [selectedSubs, setSelectedSubs] = useState([]);
  const [editingSubs, setEditingSubs] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadSubscriptions();
  }, []);

  useEffect(() => {
    filterSubscriptions();
  }, [subscriptions, searchTerm, statusFilter]);

  const loadSubscriptions = async () => {
    try {
      const response = await subscriptionsApi.getAll();
      setSubscriptions(response.data);
    } catch (error) {
      toast.error('Failed to load subscriptions');
    } finally {
      setIsLoading(false);
    }
  };

  const filterSubscriptions = () => {
    let filtered = [...subscriptions];
    
    if (searchTerm) {
      filtered = filtered.filter(sub => 
        sub.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => sub.status === statusFilter);
    }
    
    setFilteredSubs(filtered);
  };

  const handleDelete = async (id) => {
    setIsDeleting(true);
    try {
      await subscriptionsApi.delete(id);
      setSubscriptions(subscriptions.filter(s => s.id !== id));
      toast.success('Subscription deleted');
      setDeleteModal({ open: false, subscription: null });
    } catch (error) {
      toast.error('Failed to delete subscription');
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteModal = (subscription) => {
    setDeleteModal({ open: true, subscription });
  };

  // PDF Scanning handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadedFile(file);
      setScanResults(null);
    } else {
      toast.error('Please upload a PDF file');
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadedFile(file);
      setScanResults(null);
    } else {
      toast.error('Please upload a PDF file');
    }
  };

  const handleScanPdf = async () => {
    if (!uploadedFile) return;
    
    setIsScanning(true);
    try {
      const response = await scanApi.scanBankStatement(uploadedFile);
      setScanResults(response.data);
      
      if (response.data.detected && response.data.detected.length > 0) {
        const detected = response.data.detected.map(sub => ({
          ...sub,
          id: sub.id || String(Math.random())
        }));
        setEditingSubs(detected);
        setSelectedSubs(detected.filter(s => s.confidence !== 'low').map(s => s.id));
        setReviewModal({
          open: true,
          subscriptions: detected,
          source: `Bank Statement: ${uploadedFile.name}`
        });
      } else {
        toast.info('No subscriptions found in the bank statement');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan bank statement');
    } finally {
      setIsScanning(false);
    }
  };

  const clearUploadedFile = () => {
    setUploadedFile(null);
    setScanResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Email Scanning handlers
  const loadEmailConnections = async () => {
    setIsLoadingConnections(true);
    try {
      const response = await emailApi.getConnections();
      setEmailConnections(response.data.connections || []);
    } catch (error) {
      toast.error('Failed to load email connections');
    } finally {
      setIsLoadingConnections(false);
    }
  };

  const handleOpenEmailSheet = () => {
    setEmailSheetOpen(true);
    loadEmailConnections();
  };

  const handleScanEmail = async (connection) => {
    setScanningConnectionId(connection.id);
    try {
      let response;
      if (connection.provider === 'gmail') {
        response = await emailApi.scanGmail(connection.id);
      } else if (connection.provider === 'outlook') {
        response = await emailApi.scanOutlook(connection.id);
      }
      
      setEmailScanResults(prev => ({ ...prev, [connection.id]: response.data }));
      
      if (response.data.detected && response.data.detected.length > 0) {
        const detected = response.data.detected.map(sub => ({
          ...sub,
          id: sub.id || String(Math.random())
        }));
        setEditingSubs(detected);
        setSelectedSubs(detected.filter(s => s.confidence !== 'low').map(s => s.id));
        setReviewModal({
          open: true,
          subscriptions: detected,
          source: `Email: ${connection.email}`
        });
      } else {
        toast.info(`No subscriptions found in ${connection.email}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan emails');
    } finally {
      setScanningConnectionId(null);
    }
  };

  // Review Modal handlers
  const toggleSubscriptionSelection = (id) => {
    setSelectedSubs(prev => 
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const updateEditingSub = (id, field, value) => {
    setEditingSubs(prev => prev.map(sub => 
      sub.id === id ? { ...sub, [field]: value } : sub
    ));
  };

  const handleSaveSubscriptions = async () => {
    const toSave = editingSubs.filter(sub => selectedSubs.includes(sub.id));
    if (toSave.length === 0) {
      toast.error('Please select at least one subscription to add');
      return;
    }
    
    setIsSaving(true);
    try {
      await emailApi.saveScannedSubscriptions(toSave);
      toast.success(`Added ${toSave.length} subscription${toSave.length > 1 ? 's' : ''}`);
      setReviewModal({ open: false, subscriptions: [], source: '' });
      setPdfSheetOpen(false);
      setEmailSheetOpen(false);
      clearUploadedFile();
      loadSubscriptions(); // Refresh the list
    } catch (error) {
      toast.error('Failed to save subscriptions');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-[#D1FAE5] text-[#064E3B] border-0">Active</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700 border-0">Cancelled</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700 border-0">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleSubmitReport = async () => {
    if (!reportForm.service_name.trim()) {
      toast.error('Please enter the service name');
      return;
    }
    
    setIsSubmittingReport(true);
    try {
      await api.post('/reports/missed-subscription', {
        service_name: reportForm.service_name,
        estimated_amount: reportForm.estimated_amount ? parseFloat(reportForm.estimated_amount) : null,
        notes: reportForm.notes || null
      });
      toast.success('Report submitted! We\'ll look into it.');
      setShowReportModal(false);
      setReportForm({ service_name: '', estimated_amount: '', notes: '' });
    } catch (error) {
      toast.error('Failed to submit report');
    } finally {
      setIsSubmittingReport(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#064E3B]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="subscriptions-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
              Subscriptions
            </h1>
            <p className="text-gray-500 mt-1">Manage all your tracked subscriptions</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button 
              variant="outline"
              onClick={() => setPdfSheetOpen(true)}
              className="rounded-xl border-[#064E3B] text-[#064E3B] hover:bg-[#D1FAE5]"
              data-testid="scan-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Scan Bank Statement
            </Button>
            <Button 
              variant="outline"
              onClick={handleOpenEmailSheet}
              className="rounded-xl border-[#064E3B] text-[#064E3B] hover:bg-[#D1FAE5]"
              data-testid="scan-emails-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Scan Emails
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowReportModal(true)}
              className="rounded-xl border-gray-200"
              data-testid="report-missing-btn"
            >
              <AlertCircle className="w-4 h-4 mr-2" />
              Missing a subscription?
            </Button>
            <Button 
              onClick={() => navigate('/add-subscription')}
              className="bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl btn-hover"
              data-testid="add-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Subscription
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="border border-gray-100 shadow-soft">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search subscriptions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border border-gray-100 shadow-soft overflow-hidden">
          {filteredSubs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Amount</TableHead>
                  <TableHead className="font-semibold">Cycle</TableHead>
                  <TableHead className="font-semibold">Renewal</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubs.map((sub) => (
                  <TableRow 
                    key={sub.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    data-testid={`subscription-row-${sub.id}`}
                    onClick={() => navigate(`/subscriptions/${sub.id}`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#D1FAE5] rounded-xl flex items-center justify-center text-[#064E3B] font-semibold">
                          {sub.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{sub.name}</p>
                          {sub.category && (
                            <p className="text-xs text-gray-500">{sub.category}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold">${sub.amount}</span>
                      <span className="text-gray-400 text-sm ml-1">{sub.currency}</span>
                    </TableCell>
                    <TableCell className="capitalize">{sub.billing_cycle}</TableCell>
                    <TableCell>{formatDate(sub.renewal_date)}</TableCell>
                    <TableCell>{getStatusBadge(sub.status)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`action-${sub.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={() => navigate(`/subscriptions/${sub.id}`)}
                            data-testid={`view-${sub.id}`}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {sub.status === 'active' && (
                            <>
                              <DropdownMenuItem 
                                onClick={() => navigate(`/cancel/${sub.id}`)}
                                data-testid={`cancel-${sub.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Cancel Subscription
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => navigate(`/negotiate/${sub.id}`)}
                                data-testid={`negotiate-${sub.id}`}
                              >
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Negotiate Price
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => openDeleteModal(sub)}
                            className="text-red-600"
                            data-testid={`delete-${sub.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <CardContent className="py-16">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 font-medium">
                  {subscriptions.length === 0 
                    ? 'No subscriptions yet' 
                    : 'No subscriptions match your filters'
                  }
                </p>
                <p className="text-sm text-gray-400 mt-1 mb-4">
                  {subscriptions.length === 0 
                    ? 'Add your first subscription to start tracking'
                    : 'Try adjusting your search or filters'
                  }
                </p>
                {subscriptions.length === 0 && (
                  <Button 
                    onClick={() => navigate('/add-subscription')}
                    className="bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl"
                    data-testid="empty-add-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Subscription
                  </Button>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Report Missed Subscription Modal */}
        <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Report Missing Subscription</DialogTitle>
              <DialogDescription>
                Think we missed a subscription in your scan? Let us know and we&apos;ll improve our detection.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Service Name *</label>
                <Input
                  placeholder="e.g., Netflix, Spotify, Adobe..."
                  value={reportForm.service_name}
                  onChange={(e) => setReportForm({ ...reportForm, service_name: e.target.value })}
                  data-testid="report-service-name"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Estimated Amount (optional)</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g., 9.99"
                  value={reportForm.estimated_amount}
                  onChange={(e) => setReportForm({ ...reportForm, estimated_amount: e.target.value })}
                  data-testid="report-amount"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Additional Notes (optional)</label>
                <Textarea
                  placeholder="Any details that might help us find it..."
                  value={reportForm.notes}
                  onChange={(e) => setReportForm({ ...reportForm, notes: e.target.value })}
                  rows={3}
                  data-testid="report-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReportModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitReport}
                disabled={isSubmittingReport}
                className="bg-[#064E3B] hover:bg-[#053D2E] text-white"
                data-testid="submit-report-btn"
              >
                {isSubmittingReport ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
                ) : (
                  'Submit Report'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, subscription: null })}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <Trash2 className="w-5 h-5" />
                Delete Subscription
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this subscription? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {deleteModal.subscription && (
              <div className="py-4">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center text-red-600 font-semibold text-lg">
                    {deleteModal.subscription.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{deleteModal.subscription.name}</p>
                    <p className="text-sm text-gray-500">
                      ${deleteModal.subscription.amount} / {deleteModal.subscription.billing_cycle}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => setDeleteModal({ open: false, subscription: null })}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => handleDelete(deleteModal.subscription?.id)}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                {isDeleting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Subscription
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Scan PDF Sheet */}
        <Sheet open={pdfSheetOpen} onOpenChange={setPdfSheetOpen}>
          <SheetContent className="sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 text-[#064E3B]" style={{ fontFamily: 'Outfit' }}>
                <FileText className="w-5 h-5" />
                Scan Bank Statement
              </SheetTitle>
              <SheetDescription>
                Upload a PDF bank statement to find recurring subscriptions using AI.
              </SheetDescription>
            </SheetHeader>
            
            <div className="mt-6 space-y-4">
              {/* Drop Zone */}
              <div
                className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                  isDragging 
                    ? 'border-[#064E3B] bg-[#D1FAE5]/30' 
                    : uploadedFile 
                    ? 'border-[#064E3B] bg-[#D1FAE5]/10' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                data-testid="pdf-dropzone"
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf"
                  className="hidden"
                  data-testid="pdf-file-input"
                />
                
                {uploadedFile ? (
                  <div className="space-y-4">
                    <div className="w-14 h-14 bg-[#D1FAE5] rounded-xl flex items-center justify-center mx-auto">
                      <FileText className="w-7 h-7 text-[#064E3B]" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{uploadedFile.name}</p>
                      <p className="text-sm text-gray-500">
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <div className="flex items-center justify-center gap-3">
                      <Button
                        onClick={handleScanPdf}
                        disabled={isScanning}
                        className="bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl"
                        data-testid="scan-pdf-submit"
                      >
                        {isScanning ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Search className="w-4 h-4 mr-2" />
                            Scan
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={clearUploadedFile}
                        disabled={isScanning}
                        className="rounded-xl"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="space-y-3 cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto">
                      <Upload className="w-7 h-7 text-gray-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">
                        {isDragging ? 'Drop your PDF here' : 'Drag & drop your bank statement'}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        or click to browse • PDF only • Max 10MB
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Scan Results Summary */}
              {scanResults && (
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#D1FAE5] rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-[#064E3B]" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Scan Complete</p>
                      <p className="text-sm text-gray-600">
                        Found {scanResults.subscriptions_detected} subscription{scanResults.subscriptions_detected !== 1 ? 's' : ''}
                        {scanResults.limited_results && (
                          <span className="text-amber-600"> (Free tier: max 5)</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* How it works */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                <h4 className="font-medium text-blue-900 mb-2">How it works</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>1. Upload your bank statement PDF</li>
                  <li>2. AI analyzes transactions for recurring patterns</li>
                  <li>3. Review and add detected subscriptions</li>
                </ul>
                <p className="text-xs text-blue-600 mt-3">
                  🔒 Files are processed securely and never stored
                </p>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Scan Emails Sheet */}
        <Sheet open={emailSheetOpen} onOpenChange={setEmailSheetOpen}>
          <SheetContent className="sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 text-[#064E3B]" style={{ fontFamily: 'Outfit' }}>
                <Mail className="w-5 h-5" />
                Scan Emails
              </SheetTitle>
              <SheetDescription>
                Scan your connected email accounts for subscription receipts and billing notices.
              </SheetDescription>
            </SheetHeader>
            
            <div className="mt-6 space-y-4">
              {isLoadingConnections ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" />
                </div>
              ) : emailConnections.length > 0 ? (
                <div className="space-y-3">
                  {emailConnections.map((connection) => (
                    <div 
                      key={connection.id}
                      className="p-4 border border-gray-200 rounded-xl flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          connection.provider === 'gmail' ? 'bg-red-100' : 'bg-blue-100'
                        }`}>
                          <Mail className={`w-5 h-5 ${
                            connection.provider === 'gmail' ? 'text-red-600' : 'text-blue-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{connection.email}</p>
                          <p className="text-xs text-gray-500 capitalize">{connection.provider}</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleScanEmail(connection)}
                        disabled={scanningConnectionId === connection.id}
                        className="bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl"
                        size="sm"
                        data-testid={`scan-email-${connection.id}`}
                      >
                        {scanningConnectionId === connection.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Search className="w-4 h-4 mr-1" />
                            Scan
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-7 h-7 text-gray-400" />
                  </div>
                  <p className="font-medium text-gray-700">No email accounts connected</p>
                  <p className="text-sm text-gray-500 mt-1 mb-4">
                    Connect Gmail or Outlook in Settings to scan for subscriptions
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEmailSheetOpen(false);
                      navigate('/settings');
                    }}
                    className="rounded-xl"
                  >
                    Go to Settings
                  </Button>
                </div>
              )}

              {/* Email Scan Tips */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                <h4 className="font-medium text-blue-900 mb-2">Tips</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• We scan the last 30-60 days of emails</li>
                  <li>• Only subscription/billing emails are analyzed</li>
                  <li>• Connect more accounts to find all subscriptions</li>
                </ul>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Review Subscriptions Modal */}
        <Dialog open={reviewModal.open} onOpenChange={(open) => !open && setReviewModal({ open: false, subscriptions: [], source: '' })}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#064E3B] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <Search className="w-5 h-5" />
                Review Detected Subscriptions
              </DialogTitle>
              <DialogDescription>
                From: {reviewModal.source}. Select and edit subscriptions before adding them.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4 space-y-3 max-h-[50vh] overflow-y-auto">
              {editingSubs.length > 0 ? (
                editingSubs.map((sub) => (
                  <div
                    key={sub.id}
                    className={`p-4 border rounded-xl transition-all ${
                      selectedSubs.includes(sub.id) 
                        ? 'border-[#064E3B] bg-[#D1FAE5]/10' 
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedSubs.includes(sub.id)}
                        onChange={() => toggleSubscriptionSelection(sub.id)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-[#064E3B] focus:ring-[#064E3B]"
                      />
                      <div className="flex-1 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-medium text-gray-500 uppercase">Name</label>
                            <Input
                              value={sub.name || ''}
                              onChange={(e) => updateEditingSub(sub.id, 'name', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500 uppercase">Amount</label>
                            <Input
                              type="number"
                              step="0.01"
                              value={sub.amount || ''}
                              onChange={(e) => updateEditingSub(sub.id, 'amount', parseFloat(e.target.value))}
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase">Billing Cycle</label>
                          <Select
                            value={sub.billing_cycle || 'monthly'}
                            onValueChange={(value) => updateEditingSub(sub.id, 'billing_cycle', value)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="weekly">Weekly</SelectItem>
                              <SelectItem value="monthly">Monthly</SelectItem>
                              <SelectItem value="yearly">Yearly</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Confidence & Category badges */}
                        {(sub.confidence || sub.category) && (
                          <div className="flex items-center gap-2 flex-wrap">
                            {sub.confidence && (
                              <Badge className={`text-xs ${
                                sub.confidence === 'high' ? 'bg-green-100 text-green-700' :
                                sub.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {sub.confidence} confidence
                              </Badge>
                            )}
                            {sub.category && (
                              <Badge variant="outline" className="text-xs">
                                {sub.category}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-4">No subscriptions detected</p>
              )}
            </div>
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => setReviewModal({ open: false, subscriptions: [], source: '' })}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveSubscriptions}
                disabled={isSaving || selectedSubs.length === 0}
                className="bg-[#064E3B] hover:bg-[#053D2E] text-white"
                data-testid="save-subscriptions-btn"
              >
                {isSaving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add {selectedSubs.length} Subscription{selectedSubs.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Subscriptions;
